'use client';

import { c as _c } from "react/compiler-runtime";
import { Popover } from '@base-ui/react/popover';
import { css } from '@repo/pandacss/css';
import type { JsxStyleProps } from '@repo/pandacss/types';
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { MaskIcon } from '../../primitives/MaskIcon';
const toPx = (value: string | number): string => typeof value === 'number' ? `${value}px` : value;
export type InfoTooltipProps = {
  text?: ReactNode | string;
  children?: ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  ariaLabel: string;
  trigger?: ReactElement;
  rounded?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  onBlur?: () => void;
  triggerCss?: JsxStyleProps['css'];
  triggerWrapperCss?: JsxStyleProps['css'];
  triggerIconCss?: JsxStyleProps['css'];
  popupCss?: JsxStyleProps['css'];
  popupWrapperCss?: JsxStyleProps['css'];
  popupMaxWidth?: string | number;
};
const positionerBase = css({
  zIndex: 1500
});
const popupWrapperBase = css.raw({
  '--tooltip-background-color': '{colors.common.white}',
  '--tooltip-border-color': '{colors.grey.300}',
  '--tooltip-arrow-height': '[0.71em]',
  '--tooltip-arrow-margin': 'calc(var(--tooltip-arrow-height) * -1)',
  '--tooltip-max-width': '[240px]'
});
const popupBase = css.raw({
  textStyle: 'body.small',
  backgroundColor: 'var(--tooltip-background-color)',
  borderWidth: '[1px]',
  borderStyle: 'solid',
  borderColor: 'var(--tooltip-border-color)',
  borderRadius: '[0]',
  boxShadow: 'tooltip',
  color: 'text.primary',
  px: '3',
  py: '3',
  position: 'relative',
  maxWidth: 'var(--tooltip-max-width)',
  transformOrigin: '[var(--transform-origin)]',
  transitionProperty: '[opacity, transform]',
  transitionDuration: '[200ms, 133ms]',
  transitionTimingFunction: 'ease-in-out',
  '&[data-starting-style]': {
    transform: '[scale(0.75, 0.5625)]',
    opacity: '[0]'
  },
  '&[data-ending-style]': {
    transform: '[scale(0.75, 0.5625)]',
    opacity: '[0]',
    transitionProperty: '[opacity, transform]',
    transitionDuration: '[200ms, 133ms]',
    transitionDelay: '[0, 67ms]',
    transitionTimingFunction: 'ease-in-out'
  }
});
const popupRounded = css.raw({
  '--tooltip-border-color': '{colors.grey.100}',
  borderRadius: '[20px]'
});
const arrowBase = css({
  overflow: 'hidden',
  position: 'absolute',
  fontSize: '[24px]',
  width: '[1em]',
  height: 'var(--tooltip-arrow-height)',
  boxSizing: 'border-box',
  _before: {
    content: '""',
    my: 'auto',
    mx: 'auto',
    display: 'block',
    width: '[100%]',
    height: '[100%]',
    backgroundColor: 'var(--tooltip-background-color)',
    borderStyle: 'solid',
    borderWidth: '[1px]',
    borderColor: 'var(--tooltip-border-color)',
    boxSizing: 'border-box',
    transform: '[rotate(45deg)]'
  },
  '&[data-side="top"]': {
    bottom: '0',
    marginBottom: 'var(--tooltip-arrow-margin)',
    '&::before': {
      transformOrigin: '[100% 0]'
    }
  },
  '&[data-side="bottom"]': {
    top: '0',
    marginTop: 'var(--tooltip-arrow-margin)',
    '&::before': {
      transformOrigin: '[0 100%]'
    }
  },
  '&[data-side="left"]': {
    right: '0',
    marginRight: 'var(--tooltip-arrow-margin)',
    height: '[1em]',
    width: '[0.71em]',
    '&::before': {
      transformOrigin: '[0 0]'
    }
  },
  '&[data-side="right"]': {
    left: '0',
    marginLeft: 'var(--tooltip-arrow-margin)',
    height: '[1em]',
    width: '[0.71em]',
    '&::before': {
      transformOrigin: '[100% 100%]'
    }
  }
});
const triggerButtonBase = css.raw({
  cursor: 'pointer',
  backgroundColor: 'transparent',
  borderWidth: '[1px]',
  borderStyle: 'solid',
  borderColor: 'transparent',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '[18px]',
  px: '0',
  py: '0',
  position: 'relative',
  width: '[18px]',
  minHeight: '[24px]',
  minWidth: '[24px]',
  _focusVisible: {
    outlineWidth: '[2px]',
    outlineStyle: 'solid',
    outlineColor: 'blues.main',
    outlineOffset: '[1px]',
    borderRadius: 'xs'
  }
});
const triggerIconBase = css.raw({
  boxSize: 'icon.small',
  bgColor: 'grey.500'
});
export function InfoTooltip(t0) {
  const $ = _c(45);
  const {
    position: t1,
    text,
    children,
    ariaLabel,
    trigger,
    rounded: t2,
    onBlur,
    onOpen,
    onClose,
    triggerIconCss,
    triggerCss,
    triggerWrapperCss,
    popupCss,
    popupWrapperCss,
    popupMaxWidth
  } = t0;
  const position = t1 === undefined ? "top" : t1;
  const rounded = t2 === undefined ? false : t2;
  const content = text ?? children;
  if (!content) {
    return null;
  }
  let t3;
  if ($[0] !== onClose || $[1] !== onOpen) {
    t3 = open => {
      if (open) {
        onOpen?.();
      } else {
        onClose?.();
      }
    };
    $[0] = onClose;
    $[1] = onOpen;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== triggerWrapperCss) {
    t4 = css({
      display: "inline-block",
      ...triggerWrapperCss
    });
    $[3] = triggerWrapperCss;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== triggerCss) {
    t5 = css(triggerButtonBase, triggerCss);
    $[5] = triggerCss;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== trigger) {
    t6 = trigger ? <span>{trigger}</span> : undefined;
    $[7] = trigger;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== trigger || $[10] !== triggerIconCss) {
    t7 = !trigger && <MaskIcon glyph="help-outline" className={css(triggerIconBase, triggerIconCss)} />;
    $[9] = trigger;
    $[10] = triggerIconCss;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== ariaLabel || $[13] !== t5 || $[14] !== t6 || $[15] !== t7) {
    t8 = <Popover.Trigger aria-label={ariaLabel} className={t5} render={t6}>{t7}</Popover.Trigger>;
    $[12] = ariaLabel;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t4 || $[18] !== t8) {
    t9 = <span className={t4}>{t8}</span>;
    $[17] = t4;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== popupWrapperCss) {
    t10 = css(popupWrapperBase, popupWrapperCss);
    $[20] = popupWrapperCss;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== popupMaxWidth) {
    t11 = popupMaxWidth !== undefined ? {
      "--tooltip-max-width": toPx(popupMaxWidth)
    } as CSSProperties : undefined;
    $[22] = popupMaxWidth;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const t12 = rounded && popupRounded;
  let t13;
  if ($[24] !== popupCss || $[25] !== t12) {
    t13 = css(popupBase, t12, popupCss);
    $[24] = popupCss;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== onBlur) {
    t14 = () => onBlur?.();
    $[27] = onBlur;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Popover.Arrow render={<span />} className={arrowBase} aria-hidden={true} />;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== content || $[31] !== t13 || $[32] !== t14) {
    t16 = <Popover.Popup className={t13} onBlur={t14}>{content}{t15}</Popover.Popup>;
    $[30] = content;
    $[31] = t13;
    $[32] = t14;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== position || $[35] !== t16) {
    t17 = <Popover.Positioner side={position} sideOffset={10} className={positionerBase}>{t16}</Popover.Positioner>;
    $[34] = position;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t10 || $[38] !== t11 || $[39] !== t17) {
    t18 = <Popover.Portal className={t10} style={t11}>{t17}</Popover.Portal>;
    $[37] = t10;
    $[38] = t11;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t18 || $[42] !== t3 || $[43] !== t9) {
    t19 = <Popover.Root onOpenChange={t3}>{t9}{t18}</Popover.Root>;
    $[41] = t18;
    $[42] = t3;
    $[43] = t9;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  return t19;
}